import { Button, IconButton } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Icon } from '@chakra-ui/icon';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { isDragActive, motion } from 'framer-motion';
import { useState } from 'react';
import { useBoolean } from '@chakra-ui/hooks';
import { CircularProgress } from '@chakra-ui/progress';
import { AlarmIndicator, CloseCircleIcon, DotsIcons } from './UiElements';
import { Tooltip } from '@chakra-ui/tooltip';
import usePersistStore from '../stores/persistStore';
import { tooltipAnimation, tooltipDownAnimation } from './customTheme';
import React from 'react';
import TabModal from './TabModal';
import FavIcon from './FavIcon';
import ItemNote from './ItemNote';
import layoutStore from '../stores/layoutStore';
import { closeTabs } from '../../platformFiles/browserAPI';
import useStore from '../stores/store';
import { openLink, shortUrl } from '../Utils/tabUtils';
import { useItemSize } from '../Utils/useItemSize';

interface ItemProps {
    data: {
        id: string;
        url: string;
        title: string;
        favIcon: string;
        dateCreated?: string;
        tabId?: string;
        hasAlarm?: any[] | undefined;
        metaUrl?: string;
        comment?: string;
        commentColor?: boolean;
        doneStatus?: boolean;
        justAdded?: boolean;
        note?: boolean;
        todo?: boolean;
        type?: string;
        groupColor?: string;
        emoji?: string;
    };
    isActiveTab?: boolean;
    groupId?: string;
    insideActiveGroup?: string;
    hoverSite?: boolean;
    hover?: any;
    setLoadingState?: any;
    loading?: boolean;
    hideTooltip?: boolean;
    overlayItem?: boolean;
    hasAlarm?: any;
    isDiscarded?: boolean;
    isPinned?: boolean;
}

export const ItemTab: React.FC<ItemProps> = React.memo(
    ({
        groupId,
        data,
        isActiveTab = false,
        insideActiveGroup = '',
        hoverSite,
        hover,
        setLoadingState,
        loading,
        hideTooltip = false,
        overlayItem,
        hasAlarm = null,
        isDiscarded = false,
        isPinned = false
    }) => {
        const { url, title, favIcon, dateCreated, id } = data;
        const showURL = usePersistStore((state) => state.showURL);
        let setSiteModalOpen = layoutStore((state) => state.setSiteModalOpen);
        const siteModalOpen =
            layoutStore((state) => state.siteModalOpen) === id;

        const openTabModal = (e) => {
            e.stopPropagation();
            setSiteModalOpen({ groupId: groupId, ...data });
        };

        const urlText = useColorModeValue('gray.400', '#A1A2A4');
        const textColor = useColorModeValue('#1A202C', '#FFF');
        const weight = useColorModeValue('500', '600');
        const iconDarkerColor = useColorModeValue('gray.600', '#A1A2A4');
        const iconBG = useColorModeValue('transparent', '#32363A');

        const openLinkMiddle = (e) => {
            openLink(e, url, setLoadingState);
        };

        let urlToShow = shortUrl(url);

        const { fontSize, urlSize, showUrlOrNot, itemFavMr } = useItemSize();
        const showUrl = showURL && showUrlOrNot;

        return (
            <>
                <FavIcon
                    url={url}
                    favIcon={favIcon}
                    loading={loading}
                    hoverSite={hoverSite}
                    isActiveTab={isActiveTab}
                    title={title}
                    hasAlarm={hasAlarm}
                    mr={itemFavMr}
                    //isDiscarded={isDiscarded} //show as grayed out in tabsList
                    isPinned={isPinned}
                />
                {isActiveTab ? (
                    <Text
                        fontSize="13px"
                        fontWeight="500"
                        maxWidth="calc(100% - 48px)"
                        isTruncated
                        letterSpacing="0.64px"
                        mr="32px"
                    >
                        {title}
                    </Text>
                ) : (
                    <Tooltip
                        placement="top"
                        motionProps={tooltipAnimation}
                        isDisabled={hideTooltip}
                        zIndex="1000"
                        rounded="6px"
                        px="8px"
                        py="4px"
                        openDelay={160}
                        label={
                            <TooltipContent
                                title={title}
                                dateCreated={dateCreated}
                                url={url}
                            />
                        }
                    >
                        <Box
                            role="group"
                            mr="16px"
                            width={'calc(100% - 84px)'}
                            display="flex"
                            flexDirection="column"
                            opacity={loading ? '0.6' : '1'}
                            cursor={overlayItem ? 'grabbing' : 'pointer'}
                        >
                            <Box
                                onClick={openLinkMiddle}
                                // onDragStart={(e) => {
                                //     e.preventDefault();
                                // }}
                                my="auto"
                                letterSpacing="0.42px"
                                color={textColor}
                                fontWeight={weight}
                                fontSize={fontSize}
                                isTruncated
                            >
                                {title}
                            </Box>
                            {showUrl && (
                                <Box
                                    _groupHover={{
                                        textDecoration: 'underline'
                                    }}
                                    onClick={openLinkMiddle}
                                    // onDragStart={(e) => {
                                    //     e.preventDefault();
                                    // }}
                                    mt="2px"
                                    my="auto"
                                    color={urlText}
                                    fontWeight="500"
                                    fontSize={urlSize}
                                    isTruncated
                                >
                                    {urlToShow}
                                </Box>
                            )}
                        </Box>
                    </Tooltip>
                )}
                <Flex position="absolute" right="0px">
                    <Box
                        ml="4px"
                        mr="12px"
                        opacity={hoverSite && !siteModalOpen ? 1 : 0}
                        transition="0.3s cubic-bezier(0.175, 0.885, 0.320, 1.275)"
                        transform={
                            hoverSite && !siteModalOpen
                                ? 'scale(1)'
                                : 'scale(0)'
                        }
                    >
                        {overlayItem ? null : isActiveTab ? (
                            <ActiveCloseTabButton id={id} windowId={groupId} />
                        ) : (
                            <Tooltip
                                label="Edit"
                                zIndex="1000"
                                motionProps={tooltipDownAnimation}
                                placement="bottom"
                                rounded="6px"
                            >
                                <IconButton
                                    aria-label="Edit site"
                                    icon={<DotsIcons size={'14px'} />}
                                    //onClick={() => deleteNote(spaceId, tabId, "Text-snippet")}
                                    onClick={openTabModal}
                                    color={iconDarkerColor}
                                    minWidth="28px"
                                    height="28px"
                                    bg={iconBG}
                                    //returnFocusOnClose={false}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </Flex>
            </>
        );
    }
);

export default ItemTab;

const ActiveCloseTabButton = ({ id, windowId }) => {
    const sidePanelIsOpen = layoutStore((state) => state.sidePanelIsOpen);
    const iconDarkerColor = useColorModeValue('gray.600', '#A1A2A4');

    return (
        <IconButton
            opacity={sidePanelIsOpen ? 1 : 0}
            aria-label="Close tab"
            icon={<CloseCircleIcon size={'12px'} />}
            //onClick={() => deleteNote(spaceId, tabId, "Text-snippet")}
            onClick={(e) => {
                e.stopPropagation();
                closeTabs([id], windowId, true);
            }}
            color={iconDarkerColor}
            minWidth="24px"
            height="24px"
            borderRadius={'50%'}
            mr="4px"
            //returnFocusOnClose={false}
        />
    );
};

const TooltipContent = ({ title, dateCreated, url }) => {
    return (
        <Box>
            <Text
                fontSize="13"
                fontWeight="600"
                //letterSpacing="0.54px"
            >
                {title}
            </Text>
            <Text
                fontSize="12px"
                opacity="0.725"
                mt="2px"
                wordBreak="break-all"
                //isTruncated
            >
                {url}
            </Text>
            {dateCreated && (
                <Text
                    fontSize="11px"
                    opacity="0.6"
                    mt="2px"
                    wordBreak="break-all"
                    //isTruncated
                >
                    Saved {dateCreated.substring(15, 21)},{' '}
                    {dateCreated.substring(0, 15)}
                </Text>
            )}
        </Box>
    );
};
